
import { useState, useEffect } from "react";
import { Leaf } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ServiceCategory {
  id: number;
  name: string;
  display_name_en: string;
  display_name_zh?: string;
  description?: string;
  sort_order?: number;
}

interface ServiceCategoryFilterProps {
  selectedCategories: string[];
  onCategoryChange: (categoryName: string) => void;
}

const ServiceCategoryFilter = ({ selectedCategories, onCategoryChange }: ServiceCategoryFilterProps) => {
  const { t, i18n } = useTranslation('practitioners');
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const { toast } = useToast();

  // Fetch service categories from Supabase
  useEffect(() => {
    const fetchServiceCategories = async () => {
      setIsLoadingCategories(true);
      try {
        console.log("Fetching service categories...");
        const { data, error } = await supabase
          .from('service_categories')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('id', { ascending: true });

        if (error) throw error;
        
        console.log("Fetched service categories:", data);
        setServiceCategories(data || []);
      } catch (error) {
        console.error("Error fetching service categories:", error);
        toast({
          variant: 'destructive',
          title: "Failed to load service categories",
          description: "Please try refreshing the page.",
        });
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchServiceCategories();
  }, [toast]);

  // Debug selected categories
  useEffect(() => {
    console.log("Currently selected categories:", selectedCategories);
  }, [selectedCategories]);

  // Handle category selection - single selection only
  const handleCategorySelect = (categoryName: string) => {
    console.log("Selecting category:", categoryName);
    onCategoryChange(categoryName);
  };

  // Get display name based on current language
  const getDisplayName = (category: ServiceCategory) => {
    if (i18n.language === 'zh' && category.display_name_zh) {
      return category.display_name_zh;
    }
    return category.display_name_en;
  };

  return (
    <div>
      <h3 className="text-sm font-medium mb-2 flex items-center">
        <span className="bg-amber-100 p-1 rounded-full mr-2">
          <Leaf className="h-4 w-4 text-amber-600" />
        </span>
        {t('serviceCategories')}
      </h3>
      {isLoadingCategories ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-4 bg-amber-100 rounded w-3/4"></div>
          <div className="h-4 bg-amber-100 rounded w-1/2"></div>
          <div className="h-4 bg-amber-100 rounded w-2/3"></div>
        </div>
      ) : (
        <div className="space-y-2">
          <div 
            onClick={() => handleCategorySelect("all_types")}
            className={`rounded-md px-3 py-2 cursor-pointer transition-colors ${
              selectedCategories.includes("all_types") 
                ? "bg-amber-100 border border-amber-300 text-amber-800" 
                : "hover:bg-amber-50 text-gray-700"
            }`}
          >
            {t('allTypes')}
          </div>
          
          {serviceCategories.map((category) => (
            <div 
              key={category.id}
              onClick={() => handleCategorySelect(category.name)}
              className={`rounded-md px-3 py-2 cursor-pointer transition-colors ${
                selectedCategories.includes(category.name) 
                  ? "bg-amber-100 border border-amber-300 text-amber-800" 
                  : "hover:bg-amber-50 text-gray-700"
              }`}
              title={category.description || ""}
            >
              {getDisplayName(category)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceCategoryFilter;
